import axios from 'axios';

import logger from 'src/utils/logger';

import { CONFIG } from 'src/global-config';

import { JWT_STORAGE_KEY } from 'src/auth/context/jwt/constant';

import AxiosErrorController from './AxiosErrorController';

export type HttpURLType = 'get' | 'post' | 'put' | 'delete';

export interface RequestParams<R> {
  method: HttpURLType;
  url: string;
  isMultipart?: boolean;
  requestBody?: R;
  requestParams?: R;
}

// ----------------------------------------------------------------------

const axiosInstanceNew = axios.create({
  baseURL: CONFIG.serverNewUrl, // 신서버
  // Content-Type은 각 요청에서 개별적으로 설정
});

// * 신서버에만 인터셉터 적용합니다.
axiosInstanceNew.interceptors.request.use((config) => {
  const modifiedConfig = { ...config };
  const accessToken = sessionStorage.getItem(JWT_STORAGE_KEY);
  modifiedConfig.headers.ApiSecret = import.meta.env.VITE_WEB_SECRET ?? ''; // API 보안키

  if (accessToken) {
    modifiedConfig.headers.authorization = accessToken;
  }

  // FormData인 경우 Content-Type 헤더를 제거 (브라우저가 자동으로 설정)
  if (config.data instanceof FormData) {
    delete modifiedConfig.headers['Content-Type'];
  }

  // 요청 로그
  const requestLog = {
    type: '🚀 API 요청',
    method: config.method?.toUpperCase(),
    url: config.url,
    headers: {
      'Content-Type': config.headers['Content-Type'],
      ApiSecret: config.headers.ApiSecret ? '***' : undefined,
      authorization: config.headers.authorization ? '***' : undefined,
      ...Object.fromEntries(
        Object.entries(config.headers).filter(
          ([key]) => !['ApiSecret', 'authorization'].includes(key)
        )
      ),
    },
    ...(config.data && {
      requestBody:
        config.data instanceof FormData
          ? `FormData (${Array.from(config.data.entries()).length} entries)`
          : config.data,
    }),
    ...(config.params && { requestParams: config.params }),
  };
  logger.info('API Request:', requestLog);

  return modifiedConfig;
});

axiosInstanceNew.interceptors.response.use(
  (response) => {
    // 성공 응답 로그
    const responseLog = {
      type: '✅ API 응답',
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      data: response.data,
    };
    logger.info('API Response:', responseLog);

    return response;
  },
  (error) => {
    // 에러 응답 로그
    const message = error?.response?.data?.message || error?.message || 'Something went wrong!';

    const errorLog = {
      type: '❌ API 에러',
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      errorMessage: message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseHeaders: error.response?.headers,
      responseData: error.response?.data,
      requestConfig: {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        data: error.config?.data,
        params: error.config?.params,
      },
    };
    logger.error('API Error:', errorLog);

    return Promise.reject(new Error(message));
  }
);

export { axiosInstanceNew };

// ----------------------------------------------------------------------

// * 신서버용 통합 요청 함수
export const request = async <R, T>({
  method,
  url,
  requestBody,
  requestParams,
  isMultipart,
}: RequestParams<R>): Promise<T> => {
  const headers: Record<string, string> = isMultipart
    ? { 'Content-Type': 'multipart/form-data;' }
    : {};

  try {
    const response = await axiosInstanceNew.request<T>({
      method,
      url,
      data: requestBody,
      params: requestParams,
      headers,
    });
    return response.data;
  } catch (error) {
    throw new AxiosErrorController(error);
  }
};

export const endpoints = {
  partner: {
    login: '/v1/partner/login',
    info: '/v1/partner/info',
  },
  board: {
    uploadImage: '/v1/board/upload-image',
    insert: '/v1/board/insert',
    list: '/public/board/list',
    detail: '/public/board',
    update: '/v1/board',
  },

  food: {
    list: '/public/food/list',
    request: '/public/food/request',
    detail: '/public/food',
    requests: '/public/food',
    requestLike: '/public/food/requests',
    standards: '/public/food/standards',
  },
  contact: {
    create: '/v1/contact',
  },
  poopData: {
    purchases: {
      create: '/public/poop-data/purchases',
      verify: '/public/poop-data/purchases',
    },
  },
  notiHis: {
    list: '/admin/v1/noti-his/list',
    insert: '/admin/v1/noti-his/insert',
  },
  coldMail: {
    list: '/admin/v1/cold-mails/list',
    preview: '/admin/v1/cold-mails/preview',
    send: '/admin/v1/cold-mails/send',
  },
  statistics: {
    member: {
      count: '/public/member/count',
      monthly: '/public/member/monthly',
    },
    diary: {
      count: '/public/diary/count',
      countByCategory: '/public/diary/count/byCategory',
      walk: '/public/diary/walk',
      monthly: '/public/diary/monthly',
    },
    pet: {
      rankPerHouseholds: '/public/pet/rankPerHouseholds',
      rankPetKind: '/public/pet/rankPetKind',
      rankPetName: '/public/pet/rankPetName',
      rankPetBreed: '/public/pet/rankPetBreed',
    },
    admin: {
      member: {
        daily: '/admin/v1/statistics/members/daily',
        monthly: '/admin/v1/statistics/members/monthly',
      },
      subscriber: {
        daily: '/admin/v1/statistics/subscribers/daily',
        monthly: '/admin/v1/statistics/subscribers/monthly',
      },
      diary: {
        daily: '/admin/v1/statistics/diaries/daily',
        monthly: '/admin/v1/statistics/diaries/monthly',
      },
    },
  },
  share: {
    check: '/public/share/pet-his/check',
    data: '/public/share/pet-his/data',
  },
} as const;
